import React, { useState, useEffect } from 'react';
import { Paperclip } from 'lucide-react';
import { useApi } from '../hooks/useApi';

const ActivityLogTab = ({ riskId, riskType }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const apiCall = useApi();

    useEffect(() => {
        if (!riskId || !riskType) return;
        setLoading(true);
        apiCall('GET', `/allRemediations/logs/by-risk?riskId=${encodeURIComponent(riskId)}&type=${encodeURIComponent(riskType)}`)
            .then(data => setLogs(data || []))
            .catch(() => {})
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [riskId, riskType]);

    const handleDownload = async (path, fileName) => {
        try {
            const response = await apiCall('GET', `/allRemediations/logs/download?path=${encodeURIComponent(path)}&type=${encodeURIComponent(riskType)}&riskId=${encodeURIComponent(riskId)}`, null, false, 'blob');
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName || 'document');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            if (err.response?.status === 423) {
                alert('This file is being scanned for malware. Please try again shortly.');
            } else if (err.response?.status === 403) {
                alert('This file has been flagged as containing malware and cannot be downloaded.');
            } else {}
        }
    };

    const parseAsUtc = (dateStr) => {
        if (!dateStr) return null;
        // Backend returns UTC datetimes without 'Z' suffix; ensure JS treats them as UTC
        const s = String(dateStr);
        return new Date(s.endsWith('Z') || s.includes('+') || s.includes('-', 10) ? s : s + 'Z');
    };

    const sortedLogs = [...logs].sort((a, b) => parseAsUtc(b.createdAt) - parseAsUtc(a.createdAt));

    const formatTime = (dateStr) => {
        const d = parseAsUtc(dateStr);
        if (!d) return '';
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDateLabel = (dateStr) => {
        const d = parseAsUtc(dateStr);
        if (!d) return '';
        return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
    };

    const formatTargetDate = (dateStr) => {
        const d = parseAsUtc(dateStr);
        if (!d) return null;
        return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    };

    const getEventConfig = (log) => {
        const type = log.changeType || 'StatusChange';
        const isApproved = log.newStatus === 'Approved';
        const configs = {
            StatusChange:     { icon: '⚡', dot: 'bg-amber-500',   label: 'Status Changed' },
            RatingChange:     { icon: '⭐', dot: 'bg-purple-500',  label: 'Rating Changed' },
            Assign:           { icon: '👤', dot: 'bg-blue-500',    label: 'Risk Assigned' },
            Reassign:         { icon: '🔄', dot: 'bg-indigo-500',  label: 'Risk Reassigned' },
            Accepted:         { icon: '✅', dot: 'bg-green-500',   label: 'Risk Accepted' },
            ApprovalPending:  { icon: '⏳', dot: 'bg-yellow-500',  label: 'Sent for Approval' },
            ApprovalDecision: {
                icon: isApproved ? '👍' : '👎',
                dot: isApproved ? 'bg-green-500' : 'bg-red-500',
                label: isApproved ? 'Approval Approved' : 'Approval Rejected',
            },
            EvidenceUpload:   { icon: '📎', dot: 'bg-gray-500',   label: 'Evidence Uploaded' },
            JiraLinked:       { icon: '🔗', dot: 'bg-blue-600',   label: 'Jira Issue Linked' },
            ServiceNowLinked: { icon: '🔗', dot: 'bg-teal-500',   label: 'ServiceNow Incident Linked' },
        };
        return configs[type] || configs.StatusChange;
    };

    const ratingColor = (r) => {
        const map = { CRITICAL: 'text-red-500', HIGH: 'text-orange-500', MEDIUM: 'text-yellow-600', LOW: 'text-green-600', INFORMATIONAL: 'text-blue-600' };
        return map[(r || '').toUpperCase()] || 'text-gray-700';
    };

    const bold = (val, colorClass = 'text-gray-800') => (
        <span className={`font-semibold ${colorClass}`}>{val}</span>
    );

    const actorName = (log) =>
        log.changedByUserName || (log.changedByUserId != null ? `User #${log.changedByUserId}` : 'System');

    const renderEventSummary = (log) => {
        const type = log.changeType || 'StatusChange';
        const actor = actorName(log);
        const prev = log.previousStatus;
        const next = log.newStatus;
        const isApproved = next === 'Approved';

        switch (type) {
            case 'RatingChange':
                return <>{prev ? <>Risk rating changed from {bold(prev, ratingColor(prev))} to {bold(next, ratingColor(next))}</> : <>Risk rating set to {bold(next, ratingColor(next))}</>} by {bold(actor)}</>;
            case 'Assign':
                return <>Risk assigned to {bold(next)} by {bold(actor)}</>;
            case 'Reassign':
                return <>Risk reassigned from {bold(prev)} to {bold(next)} by {bold(actor)}</>;
            case 'Accepted':
                return <>Risk accepted by {bold(actor)}</>;
            case 'ApprovalPending':
                return <>Risk sent for approval by {bold(actor)}</>;
            case 'ApprovalDecision':
                return <>Approval {bold(next, isApproved ? 'text-green-600' : 'text-red-600')} by {bold(log.approvedByUserName || actor)}</>;
            case 'JiraLinked':
                return <>Jira issue {bold(next, 'text-blue-600')} linked by {bold(actor)}</>;
            case 'ServiceNowLinked':
                return <>ServiceNow incident {bold(next, 'text-teal-600')} linked by {bold(actor)}</>;
            case 'EvidenceUpload':
                return <>Evidence uploaded by {bold(actor)}</>;
            default:
                return <>{prev ? <>Status changed from {bold(prev)} to {bold(next)}</> : <>Status set to {bold(next)}</>} by {bold(actor)}</>;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-sm text-gray-500">Loading activity log...</p>
            </div>
        );
    }

    if (sortedLogs.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3 text-xl">📋</div>
                <p className="text-sm font-medium text-gray-500">No activity recorded yet</p>
                <p className="text-xs text-gray-400 mt-1">Risk events will appear here as they happen</p>
            </div>
        );
    }

    return (
        <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[4.75rem] top-3 bottom-3 w-px bg-gray-200 pointer-events-none" />

            {sortedLogs.map((log, idx) => {
                const cfg = getEventConfig(log);
                const timeStr = formatTime(log.createdAt);
                const dateLabel = formatDateLabel(log.createdAt);
                const prevDateLabel = idx > 0 ? formatDateLabel(sortedLogs[idx - 1].createdAt) : null;
                const showDateSep = idx === 0 || dateLabel !== prevDateLabel;

                return (
                    <div key={log.id ?? idx}>
                        {showDateSep && (
                            <div className={`flex items-center ${idx > 0 ? 'mt-4' : ''} mb-2`}>
                                <div className="w-[4.25rem] flex-shrink-0 pr-2 text-right">
                                    <span className="text-xs font-bold text-gray-500 whitespace-nowrap">{dateLabel}</span>
                                </div>
                                <div className="w-8 flex-shrink-0" />
                                <div className="flex-1 h-px bg-gray-200" />
                            </div>
                        )}
                        <div className="flex items-center mb-2">
                            <div className="w-[4.25rem] flex-shrink-0 text-right pr-2">
                                <span className="text-[11px] text-gray-400 whitespace-nowrap">{timeStr}</span>
                            </div>
                            <div className="w-8 flex-shrink-0 flex justify-center relative z-10">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${cfg.dot}`}>
                                    {cfg.icon}
                                </div>
                            </div>
                            <div className="flex-1 min-w-0 ml-2">
                                <p className="text-xs text-gray-600 leading-snug">{renderEventSummary(log)}</p>
                                {log.targetDate && (
                                    <p className="text-[11px] text-indigo-600 font-medium mt-0.5">
                                        📅 Target: {formatTargetDate(log.targetDate)}
                                    </p>
                                )}
                                {log.comment && (
                                    <p className="text-[11px] text-gray-400 italic mt-0.5">"{log.comment}"</p>
                                )}
                                {log.supportingDocumentName && (
                                    <button
                                        onClick={() => handleDownload(log.supportingDocumentPath, log.supportingDocumentName)}
                                        className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 hover:underline mt-0.5"
                                    >
                                        <Paperclip className="w-3 h-3" />
                                        {log.supportingDocumentName}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ActivityLogTab;
